import React, { createContext, useContext, useState, useCallback } from 'react';
import type { UploadJob } from '../types/job.types';

interface UploadContextValue {
  activeUploadCount: number;
  incrementActiveUploads: () => void;
  decrementActiveUploads: () => void;
  setActiveUploadCount: (count: number) => void;
  // Optional: expose active jobs for global read-only displays (e.g., header badge tooltip)
  activeJobs?: UploadJob[];
  setActiveJobs?: (jobs: UploadJob[]) => void;
}

const UploadContext = createContext<UploadContextValue | undefined>(undefined);

export const UploadProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [activeUploadCount, setActiveUploadCount] = useState(0);
  const [activeJobs, setActiveJobs] = useState<UploadJob[] | undefined>(
    undefined
  );

  const incrementActiveUploads = useCallback(() => {
    setActiveUploadCount(prev => prev + 1);
  }, []);

  const decrementActiveUploads = useCallback(() => {
    setActiveUploadCount(prev => Math.max(0, prev - 1));
  }, []);

  const value: UploadContextValue = {
    activeUploadCount,
    incrementActiveUploads,
    decrementActiveUploads,
    setActiveUploadCount,
    activeJobs,
    setActiveJobs,
  };

  return (
    <UploadContext.Provider value={value}>{children}</UploadContext.Provider>
  );
};

export const useUploadContext = () => {
  const context = useContext(UploadContext);
  if (context === undefined) {
    throw new Error('useUploadContext must be used within an UploadProvider');
  }
  return context;
};
